import { useCallback } from 'react';
import { useFormatDate } from '@app/datetime';
import { Assets } from '@app/ui';
import useBuildContentstackImageSrc from '@drc/hooks/useBuildContentstackImageSrc';
import type { DiscountCode, InboxMessageFields } from '../types';

const logoPath = '/message-center/discount-code-icon.svg';

/**
 * Convert the discount code data to message template structure
 */
const useDiscountCodeToMsgConverter = () => {
  const formatDataHandle = useFormatDate();
  const formatDate = useCallback(
    (date) => formatDataHandle(date, 'date_and_time'),
    [formatDataHandle],
  );

  const { imageSrc: imageUrl } = useBuildContentstackImageSrc({
    src: Assets.getUrl(logoPath),
    disableSrcSets: true,
    options: {
      width: 56,
      height: 56,
    },
  });

  return useCallback(
    (discountCode: DiscountCode): InboxMessageFields | null => {
      if (!discountCode) {
        return null;
      }

      const curTime = new Date();
      const expireTime = new Date(discountCode.expiresAt);
      const activateTime = new Date(discountCode.activatesAt);

      if (
        discountCode.expiresAt &&
        discountCode.expiresAt !== '0001-01-01T00:00:00Z' &&
        curTime > expireTime
      ) {
        return null;
      }

      const value = discountCode.discount?.value;

      if (!value) {
        return null;
      }

      let subtitle = 'Offer is';

      if (discountCode.discount?.flat) {
        subtitle += ` $${value} off`;
      } else {
        subtitle += `  ${+value * 100}% off`;

        if (discountCode.discount.upperLimit) {
          subtitle += ` with a maximum discount of $${discountCode.discount.upperLimit}`;
        }
      }

      if (discountCode.discount?.minPrice) {
        subtitle += ` limited to a minimum purchase price of $${discountCode.discount?.minPrice}`;
      }

      if (
        discountCode.activatesAt &&
        discountCode.activatesAt !== '0001-01-01T00:00:00Z' &&
        activateTime > curTime
      ) {
        if (
          discountCode.expiresAt &&
          discountCode.expiresAt !== '0001-01-01T00:00:00Z'
        ) {
          subtitle += ` on purchases made between ${formatDate(
            discountCode.activatesAt,
          )} and ${formatDate(discountCode.expiresAt)}`;
        } else {
          subtitle += ` on purchases made after ${formatDate(
            discountCode.activatesAt,
          )}`;
        }
      } else if (
        discountCode.expiresAt &&
        discountCode.expiresAt !== '0001-01-01T00:00:00Z'
      ) {
        subtitle += ` on purchases made before ${formatDate(
          discountCode.expiresAt,
        )}`;
      }

      // if (discountCode.code) {
      //   subtitle += ` by using the code - ${discountCode.code}`;
      // }

      if (discountCode.maxUsesPerUser && discountCode.maxUses) {
        const maxUses = Math.min(
          discountCode.maxUsesPerUser,
          discountCode.maxUses,
        );

        subtitle += `. This code can be used at most ${maxUses} ${
          maxUses > 1 ? 'times' : 'time'
        }`;
      }

      const verticalConstrants = Object.entries(
        discountCode.discount?.verticalDetails,
      )
        .map(([key, val]) => (val ? key : null))
        .filter(Boolean);

      if (verticalConstrants.length > 0) {
        subtitle += `. This is applicable only for ${verticalConstrants.join(
          ', ',
        )}`;

        if (
          discountCode.discount?.products &&
          discountCode.discount.products?.length > 0
        ) {
          subtitle += ` and for selected products only`;
        }
      } else if (
        discountCode.discount?.products &&
        discountCode.discount.products?.length > 0
      ) {
        subtitle += `. This is applicable for selected products only`;
      }

      subtitle += '.';

      return {
        node: {
          id: discountCode.id,
          deliveredAt: discountCode.updatedAt || '',
          title: `Use Code ${discountCode.code}` || 'Discount',
          subtitle,
          isRead: false,
          imageUrl,
          link: '',
        },
      };
    },
    [formatDate, imageUrl],
  );
};

export default useDiscountCodeToMsgConverter;
